import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ArrowLeft, Loader2, FileText, Download, Save, CheckCircle2 } from "lucide-react";
import api, { formatError } from "@/lib/api";
import { markOpened, useOpenedDocs } from "@/lib/openedDocs";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { REVIEW_CRITERIA, CHOICES, isChoice } from "@/data/formConfig";
import { DataRow, Section, FINANCIAL_FIELDS } from "@/pages/ApplicationDetail";
import AllDocumentsButton from "@/components/AllDocumentsButton";
import AiReviewCard from "@/components/AiReview";

// evaluators see the academic answers without the duration and the support institution
const ACADEMIC_FIELDS = ["university", "faculty", "study_program", "field_of_study", "degree_level", "semesters_studied",
  "remaining_exams", "gpa", "financial_support", "previous_penda"];

export default function EvaluatorReview() {
  const { t } = useTranslation();
  const CRITERIA = REVIEW_CRITERIA.map((key) => ({ key, label: t(`criteria.${key}`), choice: isChoice(key) }));
  const SCORE_CRITERIA = CRITERIA.filter((c) => !c.choice);
  const { id } = useParams();
  const navigate = useNavigate();
  const [app, setApp] = useState(null);
  const opened = useOpenedDocs();
  const [scores, setScores] = useState({});
  const [comments, setComments] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/evaluator/applications/${id}`);
      setApp(res.data);
      if (res.data.my_review) {
        setScores(res.data.my_review.scores);
        setComments(res.data.my_review.comments || "");
      }
    } catch {
      toast.error(t("evaluator.review.not_available"));
      navigate("/evaluator");
    }
  }, [id, navigate, t]);

  useEffect(() => { load(); }, [load]);

  const setScore = (c, v) => setScores((s) => ({ ...s, [c.key]: c.choice ? v : Number(v) }));

  const answered = (k) => scores[k] != null && scores[k] !== "";
  const allAnswered = CRITERIA.every((c) => answered(c.key));
  const scoreVals = SCORE_CRITERIA.filter((c) => answered(c.key)).map((c) => Number(scores[c.key]));
  const previewOverall = scoreVals.length === SCORE_CRITERIA.length
    ? Math.round((scoreVals.reduce((a, b) => a + b, 0) / SCORE_CRITERIA.length) * 10) / 10
    : null;

  const submit = async () => {
    if (!allAnswered) { toast.error(t("evaluator.review.score_all")); return; }
    setSaving(true);
    try {
      await api.post(`/evaluator/applications/${id}/review`, { scores, comments });
      toast.success(t("evaluator.review.saved"));
      navigate("/evaluator");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || t("evaluator.review.failed"));
    } finally {
      setSaving(false);
    }
  };

  const downloadFile = async (f) => {
    try {
      const res = await api.get(`/files/${f.storage_path}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      markOpened(f.storage_path);
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      toast.error(t("admin.detail.could_not_open_file"));
    }
  };

  if (!app) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }


  return (
    <div className="animate-fade-in">
      <button onClick={() => navigate("/evaluator")} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6" data-testid="eval-back-btn">
        <ArrowLeft className="w-4 h-4" /> {t("evaluator.review.back")}
      </button>

      <div className="flex items-center gap-3 mb-8 flex-wrap">
        <h1 className="font-heading text-3xl font-semibold tracking-tight">{app.name} {app.surname}</h1>
        {app.my_review && (
          <span className="inline-flex items-center gap-1 text-xs text-primary bg-primary/10 border border-primary/20 rounded-full px-3 py-1">
            <CheckCircle2 className="w-3.5 h-3.5" /> {t("evaluator.review.you_scored", { score: app.my_review.overall })}
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-7">
          <Section title={t("admin.detail.academic")}>
            {ACADEMIC_FIELDS.map((f) => <DataRow key={f} field={f} app={app} />)}
          </Section>

          <Section title={t("admin.detail.financial")}>
            {FINANCIAL_FIELDS.map((f) => <DataRow key={f} field={f} app={app} />)}
          </Section>

          {app.additional_notes && (
            <Section title={t("evaluator.review.notes")}>
              <p className="text-sm leading-relaxed text-muted-foreground">{app.additional_notes}</p>
            </Section>
          )}

          <Section title={t("admin.detail.documents", { count: (app.files || []).length })}>
            {(app.files || []).length === 0 ? (
              <p className="text-sm text-muted-foreground">{t("admin.detail.no_documents")}</p>
            ) : (
              <>
              <AllDocumentsButton path={`/evaluator/applications/${id}/documents.pdf`} />
              <div className="grid sm:grid-cols-2 gap-3">
                {app.files.map((f, i) => {
                  const seen = opened.has(f.storage_path);
                  return (
                  <button key={i} onClick={() => downloadFile(f)} className={`flex items-center gap-3 border rounded-lg p-3 hover:border-primary transition-colors text-left ${seen ? "border-emerald-300 bg-emerald-50 hover:bg-emerald-100/70" : "border-border hover:bg-primary/5"}`} data-testid={`eval-doc-${f.field}`} data-opened={seen}>
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${seen ? "bg-emerald-100" : "bg-secondary"}`}>
                      {seen ? <CheckCircle2 className="w-4 h-4 text-emerald-700" strokeWidth={1.5} /> : <FileText className="w-4 h-4 text-primary" strokeWidth={1.5} />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs text-muted-foreground">{t(`documents.${f.field}.label`, { defaultValue: f.field })}</p>
                      <p className="text-sm truncate">{f.original_filename}</p>
                      {f.from_referee && <p className="text-xs text-primary truncate">{t("admin.detail.uploaded_by_referee", { name: f.from_referee.name })}</p>}
                      {seen && <p className="text-xs text-emerald-700">{t("admin.detail.opened")}</p>}
                    </div>
                    <Download className="w-4 h-4 text-muted-foreground shrink-0" />
                  </button>
                  );
                })}
              </div>
              </>
            )}
          </Section>
        </div>

        {/* Review panel */}
        <div className="lg:col-span-5">
          <div className="sticky top-24 space-y-6">
            <div className="bg-card border border-border rounded-xl p-6">
              <h3 className="font-heading text-lg font-medium mb-1">{t("evaluator.review.your_evaluation")}</h3>
              <p className="text-xs text-muted-foreground mb-5">{t("evaluator.review.score_hint")}</p>

              {CRITERIA.map((c) => (
                <div key={c.key} className="mb-5">
                  <Label className="text-sm mb-2 block">{c.label} <span className="text-muted-foreground">{t(c.choice ? "evaluator.review.range_choice" : "evaluator.review.range")}</span></Label>
                  <Select value={answered(c.key) ? String(scores[c.key]) : ""} onValueChange={(v) => setScore(c, v)}>
                    <SelectTrigger className="h-11" data-testid={`score-${c.key}`}>
                      <SelectValue placeholder={t(c.choice ? "evaluator.review.select_choice" : "evaluator.review.select_score")} />
                    </SelectTrigger>
                    <SelectContent>
                      {c.choice
                        ? CHOICES.map((v) => (
                            <SelectItem key={v} value={v} data-testid={`score-${c.key}-${v}`}>{t(`choices.${v}`)}</SelectItem>
                          ))
                        : [1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                            <SelectItem key={n} value={String(n)} data-testid={`score-${c.key}-${n}`}>{n}</SelectItem>
                          ))}
                    </SelectContent>
                  </Select>
                </div>
              ))}

              <div className="flex items-center justify-between border-t border-border pt-4 mb-4">
                <span className="text-sm text-muted-foreground">{t("evaluator.review.overall")}</span>
                <span className="font-heading text-3xl font-semibold text-primary" data-testid="review-overall">
                  {previewOverall != null ? previewOverall : "—"}<span className="text-base text-muted-foreground"> / 10</span>
                </span>
              </div>
              <p className="text-[11px] text-muted-foreground -mt-2 mb-4">{t("evaluator.review.overall_hint")}</p>

              <Button onClick={submit} disabled={saving} className="w-full" data-testid="submit-review-btn">
                {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t("common.saving")}</> : <><Save className="w-4 h-4 mr-2" /> {app.my_review ? t("evaluator.review.update") : t("evaluator.review.submit")}</>}
              </Button>
            </div>
          </div>
        </div>

        <div className="lg:col-span-12">
          <Section title={t("evaluator.review.comments")}>
            <Textarea value={comments} onChange={(e) => setComments(e.target.value)} rows={5} placeholder={t("evaluator.review.comments_placeholder")} data-testid="review-comments" />
          </Section>
          {app.my_review && <EvaluatorAiReview appId={id} mine={app.my_review} />}
        </div>
      </div>
    </div>
  );
}

// The second reader's opinion, only once this evaluator's own review is saved (the API refuses it
// before), shown next to their own scores.
function EvaluatorAiReview({ appId, mine }) {
  const { t } = useTranslation();
  const [review, setReview] = useState(undefined);
  useEffect(() => {
    api.get(`/evaluator/applications/${appId}/ai-review`).then((r) => setReview(r.data.review)).catch(() => setReview(null));
  }, [appId]);
  if (!review) return null;
  return <AiReviewCard review={review} compare={{ label: t("admin.ai.col_you"), scores: mine.scores, overall: mine.overall }} />;
}
